import { Reducer } from 'types/context';
import { TOGGLE_MODAL } from 'state/actionTypes/modalActionTypes';
import { ApiStorageProvider } from 'types/storageProvider';
import { Styles } from 'types/styles';
import { UpdatingStorageProviderState } from '../storageProviderContext/types';
import { MyDeal } from '../DealsContext/DealsContext.types';

export enum ModalType {
  MODAL_AUTHENTICATE = 'MODAL_AUTHENTICATE',
  MODAL_AUTHENTICATE_CLIENT = 'MODAL_AUTHENTICATE_CLIENT',
  MODAL_AUTHENTICATE_LEDGER = 'MODAL_AUTHENTICATE_LEDGER',
  MODAL_AUTHENTICATE_SIGNATURE = 'MODAL_AUTHENTICATE_SIGNATURE',
  MODAL_EDIT_PROFILE = 'MODAL_EDIT_PROFILE',
  MODAL_MY_DEAL_REVIEW_MESSAGE = 'MODAL_MY_DEAL_REVIEW_MESSAGE',
  MODAL_EARLY_ACCESS = 'MODAL_EARLY_ACCESS',
  MODAL_FILSTATION_EARLY_ACCESS = 'MODAL_FILSTATION_EARLY_ACCESS',
  MODAL_ALLOWANCE = 'MODAL_ALLOWANCE',
  MODAL_FILES_UPLOAD = 'MODAL_FILES_UPLOAD',
  MODAL_CONFIRM_FILE_DELETE = 'MODAL_CONFIRM_FILE_DELETE',
  MODAL_EDIT_EMAIL = 'MODAL_EDIT_EMAIL',
  MODAL_STORAGE_PROVIDER_EDIT_PREVIEW = 'MODAL_STORAGE_PROVIDER_EDIT_PREVIEW',
  MODAL_SP_HAS_EMAIL_POPUP = 'MODAL_SP_HAS_EMAIL_POPUP',
  MODAL_STORAGE_REQUEST = 'MODAL_STORAGE_REQUEST',
  MODAL_CHECK_EMAIL_MESSAGE = 'MODAL_CHECK_EMAIL_MESSAGE',
  MODAL_INITIAL_FILECOIN_ADDRESS_CONNECT = 'MODAL_INITIAL_FILECOIN_ADDRESS_CONNECT',
  MODAL_ACCOUNT_SETTINGS = 'MODAL_ACCOUNT_SETTINGS',
  MODAL_CONNECTED_ADDRESSES = 'MODAL_CONNECTED_ADDRESSES',
  MODAL_LOGIN_REQUIRED = 'MODAL_LOGIN_REQUIRED',
  MODAL_REJECT_STORAGE_REQUEST = 'MODAL_REJECT_STORAGE_REQUEST',
  NO_MODAL = '',
}
type ModalStateType<Type extends ModalType, ModalProps> = {
  modalType: Type;
  modalProps: ModalProps;
};

export type AuthModalOptions = {
  onAuthSuccess?: () => void;
  redirectURI?: string;
  type?: 'AUTH' | 'UPDATE';
};

export type BaseProps = {
  width: number | string;
  closeModal: () => void;
  newStyles?: Styles;
  className?: string;
};
export type StorageProviderModalProps = BaseProps & {
  storageProvider: ApiStorageProvider;
};
export type StorageProviderEditPreviewModalProps = BaseProps & {
  changedValues: UpdatingStorageProviderState;
};
export type ModalMyDealLeaveReviewProps = BaseProps & {
  myDeal: MyDeal;
  rating: number;
  setRating: (rating: number) => void;
};
export type ModalConfirmDeleteFileProps = BaseProps & {
  selectedFiles: string[];
};
export type ModalCheckEmailMessageProps = BaseProps & {
  email: string;
};
export type AuthenticateModalBaseProps = BaseProps & {
  options: AuthModalOptions;
};
export type AuthModalProps = AuthenticateModalBaseProps & {
  shouldAddAddress: boolean;
};
export type AllowanceModalProps = BaseProps & {
  minerId: string;
  ownerAddress: string;
};
export type SPHasEmailModalProps = BaseProps & {
  message: string;
};
export type ModalRejectStorageRequestProps = BaseProps & {
  storageRequestId: string;
};

type AuthenticateModalState = ModalStateType<
  typeof ModalType.MODAL_AUTHENTICATE,
  AuthModalProps
>;

type AuthenticateClientModalState = ModalStateType<
  typeof ModalType.MODAL_AUTHENTICATE_CLIENT,
  AuthenticateModalBaseProps
>;

type AuthenticateLedgerModalState = ModalStateType<
  typeof ModalType.MODAL_AUTHENTICATE_LEDGER,
  AuthModalProps
>;

type AuthenticateSignatureModalState = ModalStateType<
  typeof ModalType.MODAL_AUTHENTICATE_SIGNATURE,
  AuthModalProps
>;

type StorageProviderModalState = ModalStateType<
  typeof ModalType.MODAL_EDIT_PROFILE,
  StorageProviderModalProps
>;

type ModalMyDealLeaveReviewState = ModalStateType<
  typeof ModalType.MODAL_MY_DEAL_REVIEW_MESSAGE,
  ModalMyDealLeaveReviewProps
>;

type ModalEarlyAccessState = ModalStateType<
  typeof ModalType.MODAL_EARLY_ACCESS,
  BaseProps
>;

type ModalFilstationEarlyAccessState = ModalStateType<
  typeof ModalType.MODAL_FILSTATION_EARLY_ACCESS,
  BaseProps
>;

type AllowanceModalState = ModalStateType<
  typeof ModalType.MODAL_ALLOWANCE,
  AllowanceModalProps
>;

type ModalFilesUploadState = ModalStateType<
  typeof ModalType.MODAL_FILES_UPLOAD,
  BaseProps
>;

type ModalStorageProviderEditPreviewState = ModalStateType<
  typeof ModalType.MODAL_STORAGE_PROVIDER_EDIT_PREVIEW,
  StorageProviderEditPreviewModalProps
>;

type ModalConfirmFileDeleteState = ModalStateType<
  typeof ModalType.MODAL_CONFIRM_FILE_DELETE,
  ModalConfirmDeleteFileProps
>;

type ModalEditEmailState = ModalStateType<typeof ModalType.MODAL_EDIT_EMAIL, BaseProps>;

type ModalSPHasEmailState = ModalStateType<
  typeof ModalType.MODAL_SP_HAS_EMAIL_POPUP,
  SPHasEmailModalProps
>;

type ModalStorageRequestState = ModalStateType<
  typeof ModalType.MODAL_STORAGE_REQUEST,
  BaseProps
>;

type ModalCheckEmailMessageState = ModalStateType<
  typeof ModalType.MODAL_CHECK_EMAIL_MESSAGE,
  ModalCheckEmailMessageProps
>;

type ModalConnectedAddressesState = ModalStateType<
  typeof ModalType.MODAL_CONNECTED_ADDRESSES,
  BaseProps
>;

type ModalAccountSettingsState = ModalStateType<
  typeof ModalType.MODAL_ACCOUNT_SETTINGS,
  BaseProps
>;

type ModalInitialFilecoinAddressConnectState = ModalStateType<
  typeof ModalType.MODAL_INITIAL_FILECOIN_ADDRESS_CONNECT,
  BaseProps
>;

type ModalLoginRequiredState = ModalStateType<
  typeof ModalType.MODAL_LOGIN_REQUIRED,
  BaseProps
>;

type ModalRejectStorageRequestState = ModalStateType<
  typeof ModalType.MODAL_REJECT_STORAGE_REQUEST,
  ModalRejectStorageRequestProps
>;

type EmptyModalState = ModalStateType<typeof ModalType.NO_MODAL, BaseProps>;

export type ModalState =
  | AuthenticateModalState
  | AuthenticateLedgerModalState
  | AuthenticateSignatureModalState
  | AuthenticateClientModalState
  | StorageProviderModalState
  | ModalMyDealLeaveReviewState
  | AllowanceModalState
  | ModalEarlyAccessState
  | ModalFilstationEarlyAccessState
  | ModalFilesUploadState
  | ModalConfirmFileDeleteState
  | ModalEditEmailState
  | ModalStorageProviderEditPreviewState
  | ModalSPHasEmailState
  | ModalStorageRequestState
  | ModalCheckEmailMessageState
  | ModalInitialFilecoinAddressConnectState
  | ModalConnectedAddressesState
  | ModalAccountSettingsState
  | ModalLoginRequiredState
  | ModalRejectStorageRequestState
  | EmptyModalState;

export type ModalData =
  | {
      modalProps: AuthenticateModalBaseProps;
      modalType: ModalType.MODAL_AUTHENTICATE_CLIENT;
    }
  | {
      modalProps: AuthModalProps;
      modalType: ModalType.MODAL_AUTHENTICATE;
    }
  | {
      modalProps: AuthModalProps;
      modalType: ModalType.MODAL_AUTHENTICATE_LEDGER;
    }
  | {
      modalProps: AuthModalProps;
      modalType: ModalType.MODAL_AUTHENTICATE_SIGNATURE;
    }
  | {
      modalProps: AllowanceModalProps;
      modalType: ModalType.MODAL_ALLOWANCE;
    }
  | {
      modalProps: StorageProviderModalProps;
      modalType: ModalType.MODAL_EDIT_PROFILE;
    }
  | {
      modalProps: ModalMyDealLeaveReviewProps;
      modalType: ModalType.MODAL_MY_DEAL_REVIEW_MESSAGE;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_EARLY_ACCESS;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_FILSTATION_EARLY_ACCESS;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_FILES_UPLOAD;
    }
  | {
      modalProps: ModalConfirmDeleteFileProps;
      modalType: ModalType.MODAL_CONFIRM_FILE_DELETE;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.NO_MODAL;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_EDIT_EMAIL;
    }
  | {
      modalProps: SPHasEmailModalProps;
      modalType: ModalType.MODAL_SP_HAS_EMAIL_POPUP;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_STORAGE_PROVIDER_EDIT_PREVIEW;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_STORAGE_REQUEST;
    }
  | {
      modalProps: ModalCheckEmailMessageProps;
      modalType: ModalType.MODAL_CHECK_EMAIL_MESSAGE;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_INITIAL_FILECOIN_ADDRESS_CONNECT;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_ACCOUNT_SETTINGS;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_CONNECTED_ADDRESSES;
    }
  | {
      modalProps: BaseProps;
      modalType: ModalType.MODAL_LOGIN_REQUIRED;
    }
  | {
      modalProps: ModalRejectStorageRequestProps;
      modalType: ModalType.MODAL_REJECT_STORAGE_REQUEST;
    };

export type ModalAction = {
  payload: {
    data: ModalData;
    action: boolean;
  };
  type: typeof TOGGLE_MODAL;
};

export type ModalReducer = Reducer<ModalState, ModalAction>;
